import React, { useState, useEffect } from 'react';
import maintenanceService from '../api/maintenanceService';
import Modal from '../components/Modal';
import RowActions from '../components/RowActions';
import { Eye } from 'lucide-react';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08)
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { formatNameWithRole } from '../utils/roles';

// Traducciones para el tipo de mantenimiento
const MAINTENANCE_TYPE_LABELS = {
  PREVENTIVE: 'Preventivo',
  CORRECTIVE: 'Correctivo',
};

/**
 * @description Calcula el porcentaje de cumplimiento del checklist de un reporte.
 * Se basa en 17 ítems fijos como denominador.
 * @param {Array<boolean>} checklistResults Array de booleanos con los resultados del checklist.
 * @returns {number} Porcentaje redondeado de 0 a 100.
 */
const calcularCumplimiento = (checklistResults) => {
  if (!Array.isArray(checklistResults) || checklistResults.length === 0) return 0;
  const passed = checklistResults.filter(Boolean).length;
  return Math.round((passed / 17) * 100);
};

/**
 * @description Formatea una fecha ISO al formato dd/mm/aaaa.
 * @param {string | null} dateString Fecha en formato ISO.
 * @returns {string} Fecha formateada o '—' si no existe.
 */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * @description Página principal del historial de reportes de mantenimiento.
 * Disponible para roles ADMIN y SUPERVISOR.
 * Permite visualizar todos los reportes y ver su detalle en un modal.
 * Los reportes son evidencia de trabajo de campo y solo se crean desde la
 * app mobile (POST /maintenance-orders/:id/report, rol TECHNICIAN) — un
 * ADMIN/SUPERVISOR no genera reportes, solo los consulta.
 * @returns {JSX.Element}
 */
const MaintenanceReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error de carga inicial — bloquea el render de la tabla
  const [loadError, setLoadError] = useState('');

  // Estado del modal de detalle
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Filtros de la tabla
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Carga los reportes al montar el componente
  useEffect(() => {
    fetchReports();
  }, []);

  /**
   * @description Carga la lista completa de reportes desde la API.
   */
  const fetchReports = async () => {
    setLoadError('');
    try {
      const response = await maintenanceService.getAllMaintenanceReports();
      setReports(response.data);
    } catch (error) {
      console.error('Error al cargar reportes de mantenimiento:', error);
      setLoadError('No se pudo cargar los reportes de mantenimiento. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Abre el modal de detalle de un reporte.
   * Si el reporte en tabla no trae checklist_detail, lo solicita al backend.
   * @param {object} report El reporte de la fila seleccionada.
   */
  const openDetailModal = async (report) => {
    setDetailReport(null);
    setDetailError('');
    setDetailLoading(true);
    setShowDetailModal(true);

    // Si ya viene con checklist_detail desde la lista, lo usamos directamente
    if (report.checklist_detail && Array.isArray(report.checklist_detail)) {
      setDetailReport(report);
      setDetailLoading(false);
      return;
    }

    // De lo contrario, pedimos el detalle completo al backend
    try {
      const response = await maintenanceService.getMaintenanceReportById(report.report_id);
      setDetailReport(response.data);
    } catch (error) {
      console.error('Error al cargar detalle del reporte:', error);
      setDetailError('No se pudo cargar el detalle del reporte. Intente nuevamente.');
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * @description Cierra el modal de detalle y limpia el estado asociado.
   */
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailReport(null);
    setDetailError('');
  };

  // Filtrado en memoria: combina búsqueda por texto y tipo de mantenimiento
  const filteredReports = reports.filter((report) => {
    const matchesType = typeFilter ? report.maintenance_type === typeFilter : true;
    const query = searchText.toLowerCase();
    const matchesSearch =
      query === '' ||
      report.equipment?.name?.toLowerCase().includes(query) ||
      report.technician?.username?.toLowerCase().includes(query) ||
      String(report.equipment?.elevator_number ?? '').includes(query);
    return matchesType && matchesSearch;
  });

  if (loading) return <LoadingState message="Cargando reportes de mantenimiento..." />;

  // Error de carga inicial: no tiene sentido mostrar la tabla sin datos
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div>
      <h2>Reportes de Mantenimiento</h2>

      {/* Barra de acciones: búsqueda + filtro por tipo (sin acción de crear —
          los reportes solo se generan desde la app mobile) */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Buscar por equipo, ascensor o técnico..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todos los tipos</option>
          <option value="PREVENTIVE">Preventivo</option>
          <option value="CORRECTIVE">Correctivo</option>
        </select>
      </div>

      {/* Contador de resultados visibles */}
      <p style={styles.resultCount}>
        {filteredReports.length}{' '}
        {filteredReports.length === 1 ? 'reporte encontrado' : 'reportes encontrados'}
      </p>

      {/* Estado de lista vacía */}
      {filteredReports.length === 0 ? (
        <p style={styles.emptyMessage}>
          {searchText || typeFilter
            ? 'No se encontraron reportes con los filtros aplicados.'
            : 'No hay reportes de mantenimiento registrados.'}
        </p>
      ) : (
        <div className="table-container">
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th} className="col-hide-mobile">#</th>
              <th style={styles.th}>Equipo</th>
              <th style={styles.th} className="col-hide-mobile">Ascensor</th>
              <th style={styles.th} className="col-hide-mobile">Tipo</th>
              <th style={styles.th} className="col-hide-mobile">Técnico</th>
              <th style={styles.th} className="col-numeric">% Cumplimiento</th>
              <th style={styles.th}>Fecha</th>
              <th style={styles.th} className="col-actions-sticky">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.map((report, index) => {
              const pct = calcularCumplimiento(report.checklist_results);
              return (
                <tr key={report.report_id} style={styles.row}>
                  <td style={{ ...styles.td, color: 'rgba(56,62,65,0.45)', fontSize: '0.85rem' }} className="col-hide-mobile">
                    {index + 1}
                  </td>
                  <td style={styles.td}>{report.equipment?.name || '—'}</td>
                  <td style={styles.td} className="col-hide-mobile">
                    {report.equipment?.elevator_number ?? (
                      <span style={styles.nullValue}>—</span>
                    )}
                  </td>
                  <td style={styles.td} className="col-hide-mobile">
                    <span style={getTypeBadgeStyle(report.maintenance_type)}>
                      {MAINTENANCE_TYPE_LABELS[report.maintenance_type] || report.maintenance_type}
                    </span>
                  </td>
                  <td style={styles.td} className="col-hide-mobile">
                    {/* Rol con el que hizo el reporte, no su rol actual */}
                    {report.technician?.username
                      ? formatNameWithRole(report.technician.username, report.technician_role)
                      : '—'}
                  </td>
                  <td style={styles.td} className="col-numeric">
                    <span style={getCumplimientoStyle(pct)}>{pct}%</span>
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    {formatDate(report.created_at)}
                  </td>
                  <td style={styles.td} className="col-actions-sticky">
                    {/* Sin acción destructiva en esta página — solo "Ver detalle",
                        con el mismo tratamiento visual "acción esperada" — ver RowActions.js */}
                    <RowActions
                      actions={[
                        { key: 'detail', label: 'Ver detalle', icon: Eye, onClick: () => openDetailModal(report) },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {/* Modal de detalle de un reporte */}
      <Modal
        show={showDetailModal}
        onClose={closeDetailModal}
        title={
          detailReport
            ? `Reporte #${detailReport.report_id} — Equipo: ${detailReport.equipment?.name || '—'}`
            : 'Detalle del Reporte'
        }
        modalStyle={detailStyles.modalOverride}
      >
        {detailLoading && (
          <LoadingState message="Cargando detalle..." compact />
        )}

        {detailError && (
          <p style={detailStyles.errorText}>{detailError}</p>
        )}

        {!detailLoading && !detailError && detailReport && (
          <ReportDetail report={detailReport} />
        )}
      </Modal>
    </div>
  );
};

/**
 * @description Componente interno que renderiza el contenido del modal de detalle de un reporte.
 * Muestra los datos generales y el checklist ítem por ítem.
 * @param {object} props
 * @param {object} props.report Datos completos del reporte (incluye checklist_detail).
 * @returns {JSX.Element}
 */
const ReportDetail = ({ report }) => {
  const pct = calcularCumplimiento(report.checklist_results);

  return (
    <div>
      {/* Sección: Información general */}
      <section style={detailStyles.section}>
        <h3 style={detailStyles.sectionTitle}>Información General</h3>
        <div style={detailStyles.infoGrid}>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Tipo</span>
            <span style={detailStyles.infoValue}>
              <span style={getTypeBadgeStyle(report.maintenance_type)}>
                {MAINTENANCE_TYPE_LABELS[report.maintenance_type] || report.maintenance_type}
              </span>
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Técnico</span>
            <span style={detailStyles.infoValue}>
              {report.technician?.username
                ? formatNameWithRole(report.technician.username, report.technician_role)
                : '—'}
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Fecha</span>
            <span style={detailStyles.infoValue}>{formatDate(report.created_at)}</span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>% Cumplimiento</span>
            <span style={{ ...detailStyles.infoValue, fontWeight: '600', ...getCumplimientoColorStyle(pct) }}>
              {pct}%
            </span>
          </div>
          {report.general_notes && (
            <div style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>Observaciones</span>
              <span style={{ ...detailStyles.infoValue, whiteSpace: 'pre-wrap' }}>
                {report.general_notes}
              </span>
            </div>
          )}
        </div>
      </section>

      <hr style={detailStyles.divider} />

      {/* Sección: Checklist detallado */}
      <section style={detailStyles.section}>
        <h3 style={detailStyles.sectionTitle}>
          Checklist de Mantenimiento
          <span style={detailStyles.pctBadge}>
            {report.checklist_detail?.filter((i) => i.passed).length ?? 0} / 17
          </span>
        </h3>

        {!report.checklist_detail || report.checklist_detail.length === 0 ? (
          <p style={detailStyles.emptyText}>No hay detalle de checklist disponible.</p>
        ) : (
          <ul style={detailStyles.checklistList}>
            {report.checklist_detail.map((item, index) => (
              <li key={index} style={detailStyles.checklistItem}>
                {/* Ícono visual de estado del ítem */}
                <span style={item.passed ? detailStyles.iconPassed : detailStyles.iconFailed}>
                  {item.passed ? '✓' : '✗'}
                </span>
                <span style={detailStyles.checklistItemText}>{item.item}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

/**
 * @description Genera el estilo del badge de tipo de mantenimiento.
 * @param {string} type Valor del tipo (PREVENTIVE, CORRECTIVE).
 * @returns {object} Objeto de estilos inline.
 */
const getTypeBadgeStyle = (type) => {
  const colors = {
    PREVENTIVE: { backgroundColor: '#f0f9f0', color: '#2e7d32', border: '1px solid #a5d6a7' },
    CORRECTIVE: { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' },
  };
  return {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    ...(colors[type] || { backgroundColor: '#f0f0f0', color: '#555' }),
  };
};

/**
 * @description Genera el estilo del texto de porcentaje de cumplimiento en la tabla.
 * Verde ≥80%, naranja 50-79%, rojo <50%.
 * @param {number} pct Porcentaje de cumplimiento.
 * @returns {object} Objeto de estilos inline.
 */
const getCumplimientoStyle = (pct) => ({
  fontWeight: 'bold',
  fontSize: '0.88rem',
  color: pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626',
});

/**
 * @description Devuelve solo el color para el porcentaje (usado en el modal de detalle).
 * @param {number} pct Porcentaje de cumplimiento.
 * @returns {object} Solo la propiedad color.
 */
const getCumplimientoColorStyle = (pct) => ({
  color: pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626',
});

// Estilos del modal de detalle
const detailStyles = {
  // Sobreescribe el width: 560px del componente Modal base vía prop modalStyle
  // — el checklist de 17 ítems necesita más ancho que un modal estándar.
  modalOverride: {
    width: '720px',
  },
  // loading ahora vive en LoadingState (compact), ver hallazgo de auditoría #08
  errorText: {
    color: '#c0392b',
    textAlign: 'center',
    padding: '20px 0',
  },
  section: {
    marginBottom: '8px',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 'bold',
    color: '#444',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // Badge que muestra "aprobados / total" en el encabezado del checklist
  pctBadge: {
    backgroundColor: '#e8f4fd',
    color: '#1a6496',
    border: '1px solid #bce0fd',
    borderRadius: '10px',
    padding: '1px 8px',
    fontSize: '0.78rem',
    fontWeight: 'bold',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #eee',
    margin: '16px 0',
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  infoRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'baseline',
  },
  infoLabel: {
    minWidth: '140px',
    fontSize: '0.85rem',
    color: '#888',
    flexShrink: 0,
  },
  infoValue: {
    fontSize: '0.9rem',
    color: '#333',
  },
  // Lista del checklist sin viñetas estándar
  checklistList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    maxHeight: '360px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 4px',
    borderBottom: '1px solid #f0f0f0',
  },
  checklistItemText: {
    fontSize: '0.88rem',
    color: '#444',
  },
  // Ícono verde para ítem aprobado
  iconPassed: {
    color: '#16a34a',
    fontWeight: 'bold',
    fontSize: '1rem',
    flexShrink: 0,
    width: '16px',
    textAlign: 'center',
  },
  // Ícono rojo para ítem no aprobado
  iconFailed: {
    color: '#dc2626',
    fontWeight: 'bold',
    fontSize: '1rem',
    flexShrink: 0,
    width: '16px',
    textAlign: 'center',
  },
  emptyText: {
    color: '#999',
    fontStyle: 'italic',
    fontSize: '0.88rem',
    padding: '8px 0',
  },
};

// Estilos de la página, consistentes con WorkOrders.js y Projects.js
const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  searchInput: {
    padding: '8px',
    minWidth: '260px',
    flex: '1',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  filterSelect: {
    padding: '8px',
    minWidth: '180px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  resultCount: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '8px',
  },
  // Densidad, zebra, hover y columna de acciones fija ahora viven en
  // responsive.css (.table-container table ...) — ver hallazgo de auditoría #04.
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {},
  th: {},
  row: {},
  td: {},
  nullValue: {
    color: '#bbb',
  },
  emptyMessage: {
    color: '#777',
    fontStyle: 'italic',
  },
};

export default MaintenanceReports;
